import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { TREATMENT_PLAN_BASE_PATH } from '../../../../../constants';
import { history } from '../../../../../helpers';

import type { Bays } from '../../../../../utils/types';
import type { PlanCreation, PlanCreationDependencies } from './types';

export default function usePlanCreation({
  dispatch,
  createActions,
  getAccessTokenSilently,
  clientId,
  treatmentPlanId,
  dialog,
}: PlanCreationDependencies): PlanCreation {
  const { activeDialog, closeDialog } = dialog;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createPlan = useCallback(
    async (bay: Bays, cloneSourceTreatmentPlanId: number | null) => {
      if (clientId === undefined) return;
      setIsSubmitting(true);
      const result = await dispatch(
        createActions.createTreatmentPlanV3({
          data: {
            client_id: clientId,
            bay,
            intake_questionnaire_id: null,
            clone_source_tp_id: cloneSourceTreatmentPlanId,
          },
          getAccessTokenSilently,
        }),
      );
      setIsSubmitting(false);
      if (!createActions.createTreatmentPlanV3.fulfilled.match(result)) return;
      toast.success('Treatment Plan created');
      history.navigate(`${TREATMENT_PLAN_BASE_PATH}/${result.payload.id}`);
    },
    [clientId, createActions, dispatch, getAccessTokenSilently],
  );

  const createBlankPlan = useCallback((bay: Bays) => createPlan(bay, null), [createPlan]);

  const confirmClone = useCallback(async () => {
    if (activeDialog?.kind !== 'clonePlan' || treatmentPlanId === undefined) return;
    closeDialog();
    await createPlan(activeDialog.bay, treatmentPlanId);
  }, [activeDialog, closeDialog, createPlan, treatmentPlanId]);

  return { createBlankPlan, confirmClone, isSubmitting };
}
